import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "lib/redux/store";
import type {
  FeaturedSkill,
  Resume,
  ResumeEducation,
  ResumeProfile,
  ResumeProject,
  ResumeSkills,
  ResumeCertificate,
  ResumeLanguage,
  ResumeAward,
  ResumeWorkExperience,
} from "lib/redux/types";
import { type ShowForm } from "lib/redux/settingsSlice";

export const initialProfile: ResumeProfile = {
  name: "",
  summary: "",
  email: "",
  phone: "",
  location: "",
  url1: "",
  url2: "",
};

export const initialWorkExperience: ResumeWorkExperience = {
  company: "",
  jobTitle: "",
  startDate: "",
  endDate: "",
  descriptions: [],
  id: `we-${Date.now().toString(36)}-init`,
};

export const initialEducation: ResumeEducation = {
  school: "",
  degree: "",
  gpa: "",
  startDate: "",
  endDate: "",
  descriptions: [],
  id: `edu-${Date.now().toString(36)}-init`,
};

export const initialProject: ResumeProject = {
  project: "",
  startDate: "",
  endDate: "",
  descriptions: [],
  projectLink: "",
  id: `proj-${Date.now().toString(36)}-init`,
};

export const initialFeaturedSkill: FeaturedSkill = { skill: "", rating: 4 };
export const initialFeaturedSkills: FeaturedSkill[] = Array(6).fill({
  ...initialFeaturedSkill,
});
export const initialSkills: ResumeSkills = {
  featuredSkills: initialFeaturedSkills,
  descriptions: [],
};

export const initialCertificate: ResumeCertificate = {
  certificate: "",
  certificateLink: "",
  descriptions: [],
  id: `cert-${Date.now().toString(36)}-init`,
};

export const initialLanguage: ResumeLanguage = {
  language: "",
  level: "",
  descriptions: [],
  id: `lang-${Date.now().toString(36)}-init`,
};

export const initialAward: ResumeAward = {
  award: "",
  issuer: "",
  descriptions: [],
  id: `award-${Date.now().toString(36)}-init`,
};

export const initialCustom = {
  descriptions: [],
};

export const initialResumeState: Resume = {
  id: null,
  displayName: "",
  createdOn: "",
  modifiedOn: "",
  templateId: 1,
  published: false,
  profile: initialProfile,
  workExperiences: [initialWorkExperience],
  educations: [initialEducation],
  projects: [initialProject],
  skills: initialSkills,
  certifications: [initialCertificate],
  languages: [initialLanguage],
  awards: [initialAward],
  custom: initialCustom,
  statusId: 1,
  themeColor: "#38bdf8",
  fontFamily: "Roboto",
  fontSize: "11",
  documentSize: "A4",
  formToShow: {
    workExperiences: true,
    educations: true,
    projects: true,
    skills: true,
    certifications: false,
    languages: false,
    awards: false,
    custom: false,
  },
  formToHeading: {
    workExperiences: "WORK EXPERIENCE",
    educations: "EDUCATION",
    projects: "PROJECT",
    skills: "SKILLS",
    certifications: "CERTIFICATE",
    languages: "LANGUAGE",
    awards: "AWARD",
    custom: "CUSTOM SECTION",
  },
  formsOrder: [
    "workExperiences",
    "educations",
    "projects",
    "skills",
    "certifications",
    "languages",
    "awards",
    "custom",
  ],
  leftRightFormOrder: {
    left: [],
    right: [],
  },
  profileStatus: "IGNORED",
};

// Keep the field & value type in sync with CreateHandleChangeArgsWithDescriptions (components\ResumeForm\types.ts)
export type CreateChangeActionWithDescriptions<T> = {
  idx: number;
} & (
    | {
      field: Exclude<keyof T, "descriptions">;
      value: string;
    }
    | { field: "descriptions"; value: string[] }
  );

export const resumeSlice = createSlice({
  name: "resume",
  initialState: initialResumeState,
  reducers: {
    changeDisplayName: (draft, action: PayloadAction<string>) => {
      draft.displayName = action.payload;
    },
    changeTemplateId: (draft, action: PayloadAction<number>) => {
      draft.templateId = action.payload;
    },
    changePublishedStatus: (draft, action: PayloadAction<boolean>) => {
      draft.published = action.payload;
    },
    changeStatusId: (draft, action: PayloadAction<1 | 2 | 0>) => {
      draft.statusId = action.payload;
    },
    changeProfile: (
      draft,
      action: PayloadAction<{ field: keyof ResumeProfile; value: string }>
    ) => {
      const { field, value } = action.payload;
      draft.profile[field] = value;
    },
    changeWorkExperiences: (
      draft,
      action: PayloadAction<
        CreateChangeActionWithDescriptions<ResumeWorkExperience>
      >
    ) => {
      const { idx, field, value } = action.payload;
      const workExperience = draft.workExperiences[idx];
      workExperience[field] = value as any;
    },
    changeEducations: (
      draft,
      action: PayloadAction<CreateChangeActionWithDescriptions<ResumeEducation>>
    ) => {
      const { idx, field, value } = action.payload;
      const education = draft.educations[idx];
      education[field] = value as any;
    },
    changeProjects: (
      draft,
      action: PayloadAction<CreateChangeActionWithDescriptions<ResumeProject>>
    ) => {
      const { idx, field, value } = action.payload;
      const project = draft.projects[idx];
      project[field] = value as any;
    },
    changeSkills: (
      draft,
      action: PayloadAction<
        | { field: "descriptions"; value: string[] }
        | {
          field: "featuredSkills";
          idx: number;
          skill: string;
          rating: number;
        }
      >
    ) => {
      const { field } = action.payload;
      if (field === "descriptions") {
        const { value } = action.payload;
        draft.skills.descriptions = value;
      } else {
        const { idx, skill, rating } = action.payload;
        const featuredSkill = draft.skills.featuredSkills[idx];
        featuredSkill.skill = skill;
        featuredSkill.rating = rating;
      }
    },
    changeCertificates: (
      draft,
      action: PayloadAction<
        CreateChangeActionWithDescriptions<ResumeCertificate>
      >
    ) => {
      const { idx, field, value } = action.payload;
      const certificate = draft.certifications[idx];
      certificate[field] = value as any;
    },
    changeLanguages: (
      draft,
      action: PayloadAction<CreateChangeActionWithDescriptions<ResumeLanguage>>
    ) => {
      const { idx, field, value } = action.payload;
      const language = draft.languages[idx];
      language[field] = value as any;
    },
    changeAwards: (
      draft,
      action: PayloadAction<CreateChangeActionWithDescriptions<ResumeAward>>
    ) => {
      const { idx, field, value } = action.payload;
      const award = draft.awards[idx];
      award[field] = value as any;
    },
    changeCustom: (
      draft,
      action: PayloadAction<{ field: "descriptions"; value: string[] }>
    ) => {
      const { value } = action.payload;
      draft.custom.descriptions = value;
    },
    addSectionInForm: (draft, action: PayloadAction<{ form: ShowForm }>) => {
      const { form } = action.payload;
      switch (form) {
        case "workExperiences": {
          const item = structuredClone(initialWorkExperience) as any;
          item.id = `we-${Date.now().toString(36)}-${Math.random().toString(36).slice(2,8)}`;
          draft.workExperiences.push(item);
          return draft;
        }
        case "educations": {
          const item = structuredClone(initialEducation) as any;
          item.id = `edu-${Date.now().toString(36)}-${Math.random().toString(36).slice(2,8)}`;
          draft.educations.push(item);
          return draft;
        }
        case "projects": {
          const item = structuredClone(initialProject) as any;
          item.id = `proj-${Date.now().toString(36)}-${Math.random().toString(36).slice(2,8)}`;
          draft.projects.push(item);
          return draft;
        }
        case "certifications": {
          const item = structuredClone(initialCertificate) as any;
          item.id = `cert-${Date.now().toString(36)}-${Math.random().toString(36).slice(2,8)}`;
          draft.certifications.push(item);
          return draft;
        }
        case "languages": {
          const item = structuredClone(initialLanguage) as any;
          item.id = `lang-${Date.now().toString(36)}-${Math.random().toString(36).slice(2,8)}`;
          draft.languages.push(item);
          return draft;
        }
        case "awards": {
          const item = structuredClone(initialAward) as any;
          item.id = `award-${Date.now().toString(36)}-${Math.random().toString(36).slice(2,8)}`;
          draft.awards.push(item);
          return draft;
        }
      }
    },
    moveSectionInForm: (
      draft,
      action: PayloadAction<{
        form: ShowForm;
        idx: number;
        direction: "up" | "down";
      }>
    ) => {
      const { form, idx, direction } = action.payload;
      if (form !== "skills" && form !== "custom") {
        if (
          (idx === 0 && direction === "up") ||
          (idx === draft[form].length - 1 && direction === "down")
        ) {
          return draft;
        }

        const section = draft[form][idx];
        if (direction === "up") {
          draft[form][idx] = draft[form][idx - 1];
          draft[form][idx - 1] = section;
        } else {
          draft[form][idx] = draft[form][idx + 1];
          draft[form][idx + 1] = section;
        }
      }
    },
    deleteSectionInFormByIdx: (
      draft,
      action: PayloadAction<{ form: ShowForm; idx: number }>
    ) => {
      const { form, idx } = action.payload;
      if (form !== "skills" && form !== "custom") {
        draft[form].splice(idx, 1);
      }
    },
    setResume: (draft, action: PayloadAction<Resume>) => {
      return action.payload;
    },
    resetResume: () => {
      return initialResumeState;
    },
    resetSection: (draft, action: PayloadAction<{ section: string }>) => {
      const { section } = action.payload;
      switch (section) {
        case "personalDetails": {
          draft.profile = structuredClone(initialProfile);
          break;
        }
        case "workExperiences": {
          draft.workExperiences = [structuredClone(initialWorkExperience)];
          break;
        }
        case "educations": {
          draft.educations = [structuredClone(initialEducation)];
          break;
        }
        case "projects": {
          draft.projects = [structuredClone(initialProject)];
          break;
        }
        case "skills": {
          draft.skills = structuredClone(initialSkills);
          break;
        }
        case "certifications": {
          draft.certifications = [structuredClone(initialCertificate)];
          break;
        }
        case "languages": {
          draft.languages = [structuredClone(initialLanguage)];
          break;
        }
        case "awards": {
          draft.awards = [structuredClone(initialAward)];
          break;
        }
        case "custom": {
          draft.custom = structuredClone(initialCustom);
          break;
        }
      }
    },
    changeThemeColor: (draft, action: PayloadAction<string>) => {
      draft.themeColor = action.payload;
    },
    changeFontFamily: (draft, action: PayloadAction<string>) => {
      draft.fontFamily = action.payload;
    },
    changeFontSize: (draft, action: PayloadAction<string>) => {
      draft.fontSize = action.payload;
    },
    changeDocumentSize: (draft, action: PayloadAction<string>) => {
      draft.documentSize = action.payload;
    },
    changeFormToShow: (
      draft,
      action: PayloadAction<Record<ShowForm, boolean>>
    ) => {
      draft.formToShow = action.payload;
    },
    changeFormToHeading: (
      draft,
      action: PayloadAction<Record<ShowForm, string>>
    ) => {
      draft.formToHeading = action.payload;
    },

    changeFormsOrder: (draft, action: PayloadAction<string[]>) => {
      draft.formsOrder = action.payload;
    },
    changeLeftRightFormOrder: (
      draft,
      action: PayloadAction<{
        left: ShowForm[];
        right: ShowForm[];
      }>
    ) => {
      draft.leftRightFormOrder = action.payload;
    },
    changeProfileStatus: (
      draft,
      action: PayloadAction<"CHANGED" | "DELETED" | "IGNORED">
    ) => {
      draft.profileStatus = action.payload;
    },
  },
});

export const {
  changeDisplayName,
  changeTemplateId,
  changePublishedStatus,
  changeStatusId,
  changeProfile,
  changeWorkExperiences,
  changeEducations,
  changeProjects,
  changeSkills,
  changeCertificates,
  changeLanguages,
  changeAwards,
  changeCustom,
  addSectionInForm,
  moveSectionInForm,
  deleteSectionInFormByIdx,
  setResume,
  resetResume,
  resetSection,
  changeThemeColor,
  changeFontFamily,
  changeFontSize,
  changeDocumentSize,
  changeFormToShow,
  changeFormToHeading,
  changeFormsOrder,
  changeLeftRightFormOrder,
  changeProfileStatus,
} = resumeSlice.actions;

export const selectResume = (state: RootState) => state.resume;
export const selectProfile = (state: RootState) => state.resume.profile;
export const selectWorkExperiences = (state: RootState) =>
  state.resume.workExperiences;
export const selectEducations = (state: RootState) => state.resume.educations;
export const selectProjects = (state: RootState) => state.resume.projects;
export const selectSkills = (state: RootState) => state.resume.skills;
export const selectCertificates = (state: RootState) =>
  state.resume.certifications;
export const selectLanguages = (state: RootState) => state.resume.languages;
export const selectAwards = (state: RootState) => state.resume.awards;
export const selectCustom = (state: RootState) => state.resume.custom;
export const selectProjectLink = (state: RootState, idx: number) =>
  state.resume.projects[idx].projectLink;
export const selectDisplayName = (state: RootState) => state.resume.displayName;
export const selectTemplateId = (state: RootState) => state.resume.templateId;
export const selectPublishedStatus = (state: RootState) =>
  state.resume.published;
export const selectStatusId = (state: RootState) => state.resume.statusId;
export const selectResumeId = (state: RootState) => state.resume.id;
export const selectLeftRightResumeFormOrder = (state: RootState) =>
  state.resume.leftRightFormOrder;
export default resumeSlice.reducer;
